import { AREA_CATALOG, SPECIALIST_CATALOG } from '@secretario/shared';
import { EXPRESSIONS, FAMILIES, type Expression } from '../characters.js';
import { EventType, MsgType, type MatrixClient, type MatrixEvent } from 'matrix-js-sdk';
import { useEffect, useState } from 'react';
import { announcePreview } from '../pushPreview.js';

/**
 * Mídia das conversas (T2.3.4). Em sala protegida o arquivo é cifrado no
 * aparelho (AES-CTR 256, formato `EncryptedFile` v2 do Matrix) antes de subir;
 * o servidor só guarda o conteúdo cifrado. A chave viaja dentro da mensagem.
 */
export type EncryptedFile = {
  url: string;
  v: 'v2';
  key: { alg: 'A256CTR'; k: string; key_ops: string[]; kty: 'oct'; ext: true };
  iv: string;
  hashes: { sha256: string };
};

export type MediaSource = { file?: EncryptedFile; url?: string; mime: string };
export type MediaKind = 'image' | 'video' | 'audio' | 'file' | 'sticker';

export type MessageMedia = {
  kind: MediaKind;
  name: string;
  mime: string;
  size: number;
  width: number | null;
  height: number | null;
  durationMs: number | null;
  voice: boolean;
  source: MediaSource;
  thumb: MediaSource | null;
};

/** Limite do produto por arquivo (o servidor aceita até 50 MB). */
export const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;
const IMAGE_MAX_EDGE = 1600;
const THUMB_MAX_EDGE = 420;

// ── base64 sem preenchimento (como a especificação pede) ───────────────────
function toBase64(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(binary).replace(/=+$/, '');
}

function fromBase64(text: string): Uint8Array<ArrayBuffer> {
  const binary = atob(text.replace(/-/g, '+').replace(/_/g, '/'));
  const bytes = new Uint8Array(new ArrayBuffer(binary.length));
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

// ── cifra do anexo ─────────────────────────────────────────────────────────
async function encryptAttachment(
  data: ArrayBuffer,
): Promise<{ cipher: ArrayBuffer; info: Omit<EncryptedFile, 'url'> }> {
  // Metade alta aleatória, metade baixa (contador) zerada: evita estouro do contador.
  const iv = new Uint8Array(new ArrayBuffer(16));
  crypto.getRandomValues(iv.subarray(0, 8));
  const key = await crypto.subtle.generateKey({ name: 'AES-CTR', length: 256 }, true, [
    'encrypt',
    'decrypt',
  ]);
  const cipher = await crypto.subtle.encrypt(
    { name: 'AES-CTR', counter: iv, length: 64 },
    key,
    data,
  );
  const jwk = await crypto.subtle.exportKey('jwk', key);
  const hash = await crypto.subtle.digest('SHA-256', cipher);
  return {
    cipher,
    info: {
      v: 'v2',
      key: {
        alg: 'A256CTR',
        k: jwk.k ?? '',
        key_ops: ['encrypt', 'decrypt'],
        kty: 'oct',
        ext: true,
      },
      iv: toBase64(iv),
      hashes: { sha256: toBase64(new Uint8Array(hash)) },
    },
  };
}

async function decryptAttachment(cipher: ArrayBuffer, file: EncryptedFile): Promise<ArrayBuffer> {
  const hash = toBase64(new Uint8Array(await crypto.subtle.digest('SHA-256', cipher)));
  if (hash !== file.hashes.sha256.replace(/=+$/, '')) {
    throw new Error('O arquivo recebido não confere com o que foi enviado.');
  }
  const key = await crypto.subtle.importKey('jwk', file.key, { name: 'AES-CTR' }, false, [
    'decrypt',
  ]);
  return crypto.subtle.decrypt(
    { name: 'AES-CTR', counter: fromBase64(file.iv), length: 64 },
    key,
    cipher,
  );
}

// ── subir e baixar ─────────────────────────────────────────────────────────
type Progress = (fraction: number) => void;

async function uploadBlob(
  client: MatrixClient,
  blob: Blob,
  encrypted: boolean,
  onProgress?: Progress,
): Promise<MediaSource> {
  const mime = blob.type || 'application/octet-stream';
  if (!encrypted) {
    const { content_uri: url } = await client.uploadContent(blob, {
      type: mime,
      includeFilename: false,
      ...(onProgress ? { progressHandler: (p) => onProgress(p.loaded / (p.total || 1)) } : {}),
    });
    return { url, mime };
  }
  const { cipher, info } = await encryptAttachment(await blob.arrayBuffer());
  const { content_uri: url } = await client.uploadContent(
    new Blob([cipher], { type: 'application/octet-stream' }),
    {
      type: 'application/octet-stream',
      includeFilename: false,
      ...(onProgress ? { progressHandler: (p) => onProgress(p.loaded / (p.total || 1)) } : {}),
    },
  );
  return { file: { ...info, url }, mime };
}

const objectUrls = new Map<string, Promise<string>>();

/** Baixa (com a sessão do usuário), decifra se preciso e devolve um endereço local. */
export function mediaObjectUrl(client: MatrixClient, source: MediaSource): Promise<string> {
  const mxc = source.file?.url ?? source.url;
  if (!mxc) return Promise.reject(new Error('Mídia sem endereço.'));
  const cached = objectUrls.get(mxc);
  if (cached) return cached;
  const pending = (async () => {
    const http = client.mxcUrlToHttp(mxc, undefined, undefined, undefined, false, true, true);
    if (!http) throw new Error('Endereço de mídia inválido.');
    const response = await fetch(http, {
      headers: { Authorization: `Bearer ${client.getAccessToken() ?? ''}` },
    });
    if (!response.ok) throw new Error(`Mídia indisponível (${response.status}).`);
    let data = await response.arrayBuffer();
    if (source.file) data = await decryptAttachment(data, source.file);
    return URL.createObjectURL(new Blob([data], { type: source.mime }));
  })();
  objectUrls.set(mxc, pending);
  pending.catch(() => objectUrls.delete(mxc));
  return pending;
}

/** Carrega a mídia quando `active` (ex.: miniatura na hora, vídeo só ao tocar). */
export function useMediaUrl(
  client: MatrixClient | null,
  source: MediaSource | null,
  active = true,
): { url: string | null; failed: boolean; loading: boolean } {
  const key = source?.file?.url ?? source?.url ?? null;
  const [state, setState] = useState<{ key: string | null; url: string | null; failed: boolean }>({
    key: null,
    url: null,
    failed: false,
  });
  useEffect(() => {
    if (!client || !source || !key || !active) return;
    let cancelled = false;
    mediaObjectUrl(client, source)
      .then((url) => !cancelled && setState({ key, url, failed: false }))
      .catch(() => !cancelled && setState({ key, url: null, failed: true }));
    return () => {
      cancelled = true;
    };
  }, [client, key, active]);
  const current = state.key === key ? state : { url: null, failed: false };
  return {
    url: current.url,
    failed: current.failed,
    loading: !!key && active && !current.url && !current.failed,
  };
}

// ── preparo de imagem e vídeo ──────────────────────────────────────────────
type Raster = { blob: Blob; width: number; height: number };

async function rasterize(
  source: CanvasImageSource,
  width: number,
  height: number,
  maxEdge: number,
  mime: string,
  quality: number,
): Promise<Raster> {
  const scale = Math.min(1, maxEdge / Math.max(width, height));
  const w = Math.max(1, Math.round(width * scale));
  const h = Math.max(1, Math.round(height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Não consegui preparar a imagem.');
  context.drawImage(source, 0, 0, w, h);
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, mime, quality));
  if (!blob) throw new Error('Não consegui preparar a imagem.');
  return { blob, width: w, height: h };
}

async function videoFrame(
  file: Blob,
): Promise<{ thumb: Raster | null; width: number; height: number; durationMs: number }> {
  const url = URL.createObjectURL(file);
  try {
    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';
    video.src = url;
    await new Promise<void>((resolve, reject) => {
      video.onloadeddata = () => resolve();
      video.onerror = () => reject(new Error('vídeo ilegível'));
      setTimeout(() => reject(new Error('tempo esgotado')), 8000);
    });
    const width = video.videoWidth;
    const height = video.videoHeight;
    const durationMs = Number.isFinite(video.duration) ? Math.round(video.duration * 1000) : 0;
    let thumb: Raster | null = null;
    try {
      thumb = await rasterize(video, width, height, THUMB_MAX_EDGE, 'image/jpeg', 0.7);
    } catch {
      thumb = null;
    }
    return { thumb, width, height, durationMs };
  } finally {
    URL.revokeObjectURL(url);
  }
}

async function audioDuration(file: Blob): Promise<number> {
  const url = URL.createObjectURL(file);
  try {
    const audio = new Audio();
    audio.preload = 'metadata';
    audio.src = url;
    await new Promise<void>((resolve) => {
      audio.onloadedmetadata = () => resolve();
      audio.onerror = () => resolve();
      setTimeout(resolve, 4000);
    });
    return Number.isFinite(audio.duration) ? Math.round(audio.duration * 1000) : 0;
  } finally {
    URL.revokeObjectURL(url);
  }
}

// ── enviar ─────────────────────────────────────────────────────────────────
export type SendMediaOptions = {
  voice?: boolean;
  durationMs?: number;
  replyToEventId?: string;
  onProgress?: Progress;
};

function sourceFields(source: MediaSource): Record<string, unknown> {
  return source.file ? { file: source.file } : { url: source.url };
}

export function kindOfMime(mime: string): MediaKind {
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('video/')) return 'video';
  if (mime.startsWith('audio/')) return 'audio';
  return 'file';
}

export async function sendMedia(
  client: MatrixClient,
  roomId: string,
  file: File | Blob,
  options: SendMediaOptions = {},
): Promise<void> {
  if (file.size > MAX_UPLOAD_BYTES) {
    throw new Error('Esse arquivo passa de 25 MB. Envie uma versão menor.');
  }
  // O aviso de quem recebe diz o que chegou (foto, voz, arquivo), sem o conteúdo.
  const announced = options.voice ? 'audio' : kindOfMime(file.type || 'application/octet-stream');
  await announcePreview(
    client,
    roomId,
    announced === 'image'
      ? 'Foto'
      : announced === 'video'
        ? 'Vídeo'
        : announced === 'audio'
          ? 'Mensagem de voz'
          : 'Arquivo',
  );
  const encrypted = client.isRoomEncrypted(roomId);
  const mime = file.type || 'application/octet-stream';
  const name = file instanceof File && file.name ? file.name : `arquivo-${Date.now()}`;
  const kind = options.voice ? 'audio' : kindOfMime(mime);
  const info: Record<string, unknown> = { mimetype: mime, size: file.size };
  let payload: Blob = file;

  if (kind === 'image') {
    const bitmap = await createImageBitmap(file);
    try {
      const thumb = await rasterize(
        bitmap,
        bitmap.width,
        bitmap.height,
        THUMB_MAX_EDGE,
        'image/jpeg',
        0.7,
      );
      // GIF fica como veio (animação); as demais são reduzidas para o envio.
      if (mime !== 'image/gif' && Math.max(bitmap.width, bitmap.height) > IMAGE_MAX_EDGE) {
        const outMime = mime === 'image/png' ? 'image/png' : 'image/jpeg';
        const reduced = await rasterize(
          bitmap,
          bitmap.width,
          bitmap.height,
          IMAGE_MAX_EDGE,
          outMime,
          0.86,
        );
        payload = reduced.blob;
        info.mimetype = outMime;
        info.size = reduced.blob.size;
        info.w = reduced.width;
        info.h = reduced.height;
      } else {
        info.w = bitmap.width;
        info.h = bitmap.height;
      }
      const thumbSource = await uploadBlob(client, thumb.blob, encrypted);
      Object.assign(
        info,
        thumbSource.file
          ? { thumbnail_file: thumbSource.file }
          : { thumbnail_url: thumbSource.url },
        {
          thumbnail_info: {
            mimetype: 'image/jpeg',
            size: thumb.blob.size,
            w: thumb.width,
            h: thumb.height,
          },
        },
      );
    } finally {
      bitmap.close();
    }
  } else if (kind === 'video') {
    try {
      const frame = await videoFrame(file);
      info.w = frame.width;
      info.h = frame.height;
      info.duration = frame.durationMs;
      if (frame.thumb) {
        const thumbSource = await uploadBlob(client, frame.thumb.blob, encrypted);
        Object.assign(
          info,
          thumbSource.file
            ? { thumbnail_file: thumbSource.file }
            : { thumbnail_url: thumbSource.url },
          {
            thumbnail_info: {
              mimetype: 'image/jpeg',
              size: frame.thumb.blob.size,
              w: frame.thumb.width,
              h: frame.thumb.height,
            },
          },
        );
      }
    } catch {
      /* sem miniatura: o vídeo segue como arquivo reproduzível */
    }
  } else if (kind === 'audio') {
    info.duration = options.durationMs ?? (await audioDuration(file));
  }

  const source = await uploadBlob(
    client,
    payload.type ? payload : new Blob([payload], { type: String(info.mimetype) }),
    encrypted,
    options.onProgress,
  );
  const msgtype =
    kind === 'image'
      ? MsgType.Image
      : kind === 'video'
        ? MsgType.Video
        : kind === 'audio'
          ? MsgType.Audio
          : MsgType.File;
  const content: Record<string, unknown> = {
    msgtype,
    body: options.voice ? 'Mensagem de voz' : name,
    filename: name,
    info,
    ...sourceFields(source),
  };
  if (options.voice) {
    content['org.matrix.msc3245.voice'] = {};
    content['org.matrix.msc1767.audio'] = { duration: info.duration };
  }
  if (options.replyToEventId) {
    content['m.relates_to'] = { 'm.in_reply_to': { event_id: options.replyToEventId } };
  }
  await client.sendMessage(roomId, content as never);
}

// ── figurinhas da casa (personagens do Secretário) ─────────────────────────
export type Sticker = { key: string; label: string; src: string };

export type StickerPack = { id: string; label: string; cover: string; stickers: Sticker[] };

const STICKER_MOODS: Record<Expression, string> = {
  simpatico: 'oi',
  divertido: 'rindo',
  tranquilo: 'de boa',
  direto: 'sério',
  espontaneo: 'animado',
  elegante: 'elegante',
};

/** Um grupo por personagem do elenco (com as seis expressões) e um da Equipe. */
export const STICKER_PACKS: StickerPack[] = [
  ...FAMILIES.flatMap((family) =>
    Object.values(family.variants).flatMap((variant) => {
      if (!variant) return [];
      const stickers = EXPRESSIONS.map((mood) => ({
        key: `${variant.key}-${mood}`,
        label: `${variant.name} ${STICKER_MOODS[mood]}`,
        src: `/characters/${variant.key}-${mood}.webp`,
      }));
      return [
        {
          id: variant.key,
          label: variant.name,
          cover: `/characters/${variant.key}-simpatico.webp`,
          stickers,
        },
      ];
    }),
  ),
  {
    id: 'areas',
    label: 'Sua equipe',
    cover: '/characters/areas/area-zeca-hero.webp',
    stickers: AREA_CATALOG.flatMap((area) =>
      (['hero', 'falando', 'entregando'] as const).map((pose) => ({
        key: `areas-${area.character.key}-${pose}`,
        label: area.character.name,
        src: `/characters/areas/${area.character.key}-${pose}.webp`,
      })),
    ),
  },
  // A turma antiga continua: figurinhas já enviadas apontam para estas chaves.
  {
    id: 'equipe',
    label: 'Turma',
    cover: '/characters/specialists/esp-bomdia-v2.webp',
    stickers: SPECIALIST_CATALOG.map((specialist) => ({
      key: `equipe-${specialist.mascot.key}`,
      label: specialist.mascot.name,
      src: `/characters/specialists/${specialist.mascot.key}.webp`,
    })),
  },
];

export const STICKERS: Sticker[] = STICKER_PACKS.flatMap((pack) => pack.stickers);

const RECENT_STICKERS_KEY = 'secretario.figurinhas.recentes';
const RECENT_STICKERS_MAX = 16;

/** Figurinhas usadas por último neste aparelho, da mais recente para a mais antiga. */
export function recentStickers(): Sticker[] {
  try {
    const keys = JSON.parse(window.localStorage.getItem(RECENT_STICKERS_KEY) ?? '[]') as unknown;
    if (!Array.isArray(keys)) return [];
    return keys.flatMap((key) => {
      const sticker = STICKERS.find((item) => item.key === key);
      return sticker ? [sticker] : [];
    });
  } catch {
    return [];
  }
}

export function rememberSticker(sticker: Sticker): void {
  try {
    const keys = [
      sticker.key,
      ...recentStickers()
        .map((item) => item.key)
        .filter((key) => key !== sticker.key),
    ].slice(0, RECENT_STICKERS_MAX);
    window.localStorage.setItem(RECENT_STICKERS_KEY, JSON.stringify(keys));
  } catch {
    /* sem armazenamento: só não aparece em Recentes */
  }
}

const STICKER_CACHE_KEY = 'secretario.stickers.v1';

function readStickerCache(): Record<string, { source: MediaSource; size: number }> {
  try {
    return JSON.parse(localStorage.getItem(STICKER_CACHE_KEY) ?? '{}') as Record<
      string,
      { source: MediaSource; size: number }
    >;
  } catch {
    return {};
  }
}

export async function sendSticker(
  client: MatrixClient,
  roomId: string,
  sticker: Sticker,
): Promise<void> {
  await announcePreview(client, roomId, 'Figurinha');
  const encrypted = client.isRoomEncrypted(roomId);
  const cacheKey = `${sticker.key}:${encrypted ? 'e' : 'p'}`;
  const cache = readStickerCache();
  let entry = cache[cacheKey];
  if (!entry) {
    const response = await fetch(sticker.src);
    if (!response.ok) throw new Error('Figurinha indisponível.');
    const blob = await response.blob();
    entry = { source: await uploadBlob(client, blob, encrypted), size: blob.size };
    try {
      localStorage.setItem(STICKER_CACHE_KEY, JSON.stringify({ ...cache, [cacheKey]: entry }));
    } catch {
      /* sem espaço: a figurinha sobe de novo na próxima vez */
    }
  }
  await client.sendEvent(roomId, EventType.Sticker, {
    body: sticker.label,
    info: { mimetype: 'image/webp', size: entry.size, w: 512, h: 512 },
    ...sourceFields(entry.source),
  } as never);
}

// ── foto do grupo (estado da sala não é cifrado: sobe em claro, reduzida) ──
export async function setRoomPhoto(
  client: MatrixClient,
  roomId: string,
  file: Blob,
): Promise<void> {
  const bitmap = await createImageBitmap(file);
  try {
    const side = Math.min(bitmap.width, bitmap.height);
    const canvas = document.createElement('canvas');
    canvas.width = 320;
    canvas.height = 320;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Não consegui preparar a foto.');
    context.drawImage(
      bitmap,
      (bitmap.width - side) / 2,
      (bitmap.height - side) / 2,
      side,
      side,
      0,
      0,
      320,
      320,
    );
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', 0.86),
    );
    if (!blob) throw new Error('Não consegui preparar a foto.');
    const source = await uploadBlob(client, blob, false);
    await client.sendStateEvent(roomId, EventType.RoomAvatar, { url: source.url } as never, '');
  } finally {
    bitmap.close();
  }
}

// ── ler a mídia de uma mensagem recebida ───────────────────────────────────
type MediaContent = {
  msgtype?: string;
  body?: string;
  filename?: string;
  url?: string;
  file?: EncryptedFile;
  info?: {
    mimetype?: string;
    size?: number;
    w?: number;
    h?: number;
    duration?: number;
    thumbnail_url?: string;
    thumbnail_file?: EncryptedFile;
    thumbnail_info?: { mimetype?: string };
  };
  'org.matrix.msc3245.voice'?: unknown;
};

const KIND_BY_MSGTYPE: Record<string, MediaKind> = {
  [MsgType.Image]: 'image',
  [MsgType.Video]: 'video',
  [MsgType.Audio]: 'audio',
  [MsgType.File]: 'file',
};

export function mediaOf(event: MatrixEvent): MessageMedia | null {
  const content = event.getContent<MediaContent>();
  const sticker = event.getType() === EventType.Sticker;
  const kind = sticker ? 'sticker' : KIND_BY_MSGTYPE[content.msgtype ?? ''];
  if (!kind || (!content.file && !content.url)) return null;
  const info = content.info ?? {};
  const mime = info.mimetype ?? (sticker ? 'image/webp' : 'application/octet-stream');
  const thumbMime = info.thumbnail_info?.mimetype ?? 'image/jpeg';
  return {
    kind,
    name: content.filename ?? content.body ?? 'arquivo',
    mime,
    size: info.size ?? 0,
    width: info.w ?? null,
    height: info.h ?? null,
    durationMs: info.duration ?? null,
    voice: content['org.matrix.msc3245.voice'] !== undefined,
    source: {
      mime,
      ...(content.file ? { file: content.file } : {}),
      ...(content.url ? { url: content.url } : {}),
    },
    thumb: info.thumbnail_file
      ? { file: info.thumbnail_file, mime: thumbMime }
      : info.thumbnail_url
        ? { url: info.thumbnail_url, mime: thumbMime }
        : null,
  };
}

/** Texto curto da mídia para a lista de conversas e para citações. */
export function mediaLabel(media: MessageMedia): string {
  if (media.kind === 'sticker') return 'Figurinha';
  if (media.kind === 'image') return media.mime === 'image/gif' ? 'GIF' : 'Foto';
  if (media.kind === 'video') return 'Vídeo';
  if (media.kind === 'audio') return media.voice ? 'Mensagem de voz' : 'Áudio';
  return media.name;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
}

export function formatDuration(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}
